import * as React from 'react'
import cn from 'classnames'
import { CaretDown } from '@phosphor-icons/react'
import { Editor } from 'components/atoms/editor'
import {
  QuestionRow,
  QuestionCreationCard,
  AddQuestionButton,
  EmptyState,
  CheckinQuestion,
  Team,
} from 'components/checkin-questions'
import { CheckinQuestionType } from './types'
import { OrgTeam } from './step-two'
import { fieldLabelClass } from './form-helpers'

// A question as held in the cycle builder's working state. `id` is a positive
// server id for library-loaded questions, or a negative placeholder for ones the
// admin adds in this flow (ids are stripped on submit — the whole set is
// snapshotted with the cycle). `teamId` null => an org-wide default; otherwise a
// team-specific override.
export type CycleQuestion = {
  id: number
  questionType: CheckinQuestionType
  body: string
  teamId: string | null
}

type QuestionTab = 'default' | 'team'

type StepThreeProps = {
  instructions: string
  onInstructionsChange: (html: string) => void
  questions: CycleQuestion[]
  // teamIds is [null] for an org-wide default, or one entry per selected team for
  // team-specific questions (the shared card can target several teams at once).
  onAddQuestion: (input: {
    questionType: CheckinQuestionType
    body: string
    teamIds: (string | null)[]
  }) => void
  onUpdateQuestion: (id: number, body: string) => void
  onDeleteQuestion: (id: number) => void
  questionsLoading: boolean
  questionsLoadError: string | null
  participatingTeams: OrgTeam[]
  orgSlug: string
  onViewRatingScales: () => void
}

// Keep the selected team valid as the participating set changes.
function useValidSelectedTeam(
  participatingTeams: OrgTeam[],
  selectedTeamId: string,
  setSelectedTeamId: React.Dispatch<React.SetStateAction<string>>,
) {
  React.useEffect(() => {
    const stillValid = participatingTeams.some(
      (team) => team.id === selectedTeamId,
    )
    if (participatingTeams.length > 0 && !stillValid) {
      setSelectedTeamId(participatingTeams[0].id)
    }
  }, [participatingTeams, selectedTeamId, setSelectedTeamId])
}

function isQuestionVisible(
  question: CycleQuestion,
  tab: QuestionTab,
  selectedTeamId: string,
) {
  if (tab === 'default') return question.teamId === null
  return question.teamId === selectedTeamId
}

// The shared QuestionRow works in server-id (number) space; the wizard's working
// question maps straight across (new questions carry negative ids). position is
// unused by the row's display, so a placeholder is fine.
const asCheckinQuestion = (question: CycleQuestion): CheckinQuestion => ({
  id: question.id,
  body: question.body,
  questionType: question.questionType,
  position: 0,
})

type QuestionsPanelProps = {
  questionsLoading: boolean
  questionsLoadError: string | null
  visibleQuestions: CycleQuestion[]
  adding: boolean
  tab: QuestionTab
  selectedTeamId: string
  sharedTeams: Team[]
  orgSlug: string
  onUpdateQuestion: (id: number, body: string) => void
  onDeleteQuestion: (id: number) => void
  onCreate: (input: {
    body: string
    questionType: CheckinQuestionType
    teamIds: number[]
  }) => Promise<void>
  onCancelCreate: () => void
  onStartCreate: () => void
  onViewRatingScales: () => void
}

// The questions list (or its loading/error/empty state) plus the add-question
// control. Pulled out of StepThree so this state machine is scored — and read —
// on its own instead of folded into the step's overall render tree.
const QuestionsPanel = ({
  questionsLoading,
  questionsLoadError,
  visibleQuestions,
  adding,
  tab,
  selectedTeamId,
  sharedTeams,
  orgSlug,
  onUpdateQuestion,
  onDeleteQuestion,
  onCreate,
  onCancelCreate,
  onStartCreate,
  onViewRatingScales,
}: QuestionsPanelProps) => {
  if (questionsLoading) {
    return <p className="text-[15px] text-[#5E6580]">Loading questions…</p>
  }
  if (questionsLoadError) {
    return <p className="text-[15px] text-[#E42309]">{questionsLoadError}</p>
  }

  // QuestionRow expects a Promise; the wizard's update is a synchronous local-state
  // edit, so resolve immediately.
  const handleUpdate = (id: number, body: string) => {
    onUpdateQuestion(id, body)
    return Promise.resolve()
  }

  let questionsListContent: React.ReactNode
  if (visibleQuestions.length === 0 && !adding) {
    const emptyMessage =
      tab === 'default'
        ? 'No default questions configured yet.'
        : 'No questions for this team yet.'
    questionsListContent = <EmptyState>{emptyMessage}</EmptyState>
  } else {
    questionsListContent = visibleQuestions.map((question) => (
      <QuestionRow
        key={question.id}
        question={asCheckinQuestion(question)}
        onUpdate={handleUpdate}
        onRemove={onDeleteQuestion}
      />
    ))
  }

  let creationControl: React.ReactNode
  if (adding) {
    const teamsForCard = tab === 'team' ? sharedTeams : undefined
    let initialTeamIds: number[] | undefined
    if (tab === 'team' && selectedTeamId) {
      initialTeamIds = [Number(selectedTeamId)]
    }
    creationControl = (
      <QuestionCreationCard
        ratingScaleLabels={[]}
        orgSlug={orgSlug}
        onCreate={onCreate}
        onCancel={onCancelCreate}
        onViewRatingScales={onViewRatingScales}
        teams={teamsForCard}
        initialTeamIds={initialTeamIds}
      />
    )
  } else {
    creationControl = <AddQuestionButton onClick={onStartCreate} />
  }

  return (
    <div className="flex flex-col gap-2">
      {questionsListContent}
      {creationControl}
    </div>
  )
}

// Step 3 reuses the shared check-in question UI (QuestionRow / QuestionCreationCard)
// so the wizard and the org-level Performance Cycle page stay pixel-identical. The
// wizard keeps its own Default / Team-overrides tab shell; only the rows and the
// creation card are shared.
export const StepThree = ({
  instructions,
  onInstructionsChange,
  questions,
  onAddQuestion,
  onUpdateQuestion,
  onDeleteQuestion,
  questionsLoading,
  questionsLoadError,
  participatingTeams,
  orgSlug,
  onViewRatingScales,
}: StepThreeProps) => {
  const [tab, setTab] = React.useState<QuestionTab>('default')
  const [selectedTeamId, setSelectedTeamId] = React.useState(
    participatingTeams[0]?.id ?? '',
  )
  const [adding, setAdding] = React.useState(false)

  useValidSelectedTeam(participatingTeams, selectedTeamId, setSelectedTeamId)

  // Switching tabs closes any half-filled creation card so it never carries over
  // into the other tab's context.
  const switchTab = (next: QuestionTab) => {
    setTab(next)
    setAdding(false)
  }

  const visibleQuestions = questions.filter((question) =>
    isQuestionVisible(question, tab, selectedTeamId),
  )

  // The shared creation card speaks number team ids; the wizard tracks them as
  // strings (matching OrgTeam and the team <select>), so convert at the boundary.
  const sharedTeams: Team[] = participatingTeams.map((team) => ({
    id: Number(team.id),
    name: team.name,
  }))

  const handleCreate = (input: {
    body: string
    questionType: CheckinQuestionType
    teamIds: number[]
  }) => {
    onAddQuestion({
      questionType: input.questionType,
      body: input.body,
      teamIds:
        tab === 'default' ? [null] : input.teamIds.map((id) => String(id)),
    })
    setAdding(false)
    return Promise.resolve()
  }

  const noTeams = tab === 'team' && participatingTeams.length === 0

  return (
    <>
      <div className="mb-6">
        <p className={fieldLabelClass}>Instruction to participants</p>
        <div className="rounded-[4px] bg-[#EFF2F9] p-2">
          <Editor
            toolbar="static"
            textSize="base"
            initialContent={instructions}
            placeholder="Add any guidance participants should read before starting…"
            rows={5}
            onChange={onInstructionsChange}
          />
        </div>
      </div>

      <div>
        <p className={fieldLabelClass}>Custom performance questions</p>

        <div className="mb-5 inline-flex rounded-lg bg-[#F0F2F8] p-1">
          <button
            type="button"
            onClick={() => switchTab('default')}
            aria-pressed={tab === 'default'}
            className={cn(
              'rounded-md px-5 py-2 text-center font-talent text-[16px] font-medium leading-[20px] transition-colors',
              tab === 'default'
                ? 'bg-white text-[#1D264A] shadow-sm'
                : 'text-[#5E6580] hover:text-[#1D264A]',
            )}
          >
            Default
          </button>
          <button
            type="button"
            onClick={() => switchTab('team')}
            aria-pressed={tab === 'team'}
            className={cn(
              'rounded-md px-5 py-2 text-center font-talent text-[16px] font-medium leading-[20px] transition-colors',
              tab === 'team'
                ? 'bg-white text-[#1D264A] shadow-sm'
                : 'text-[#5E6580] hover:text-[#1D264A]',
            )}
          >
            Team overrides
          </button>
        </div>

        {noTeams ? (
          <p className="mb-4 text-[15px] text-[#5E6580]">
            No teams are participating in this cycle, so there are no
            team-specific questions to configure.
          </p>
        ) : (
          <>
            {tab === 'team' && (
              <div className="mb-6">
                <label htmlFor="question-team" className={fieldLabelClass}>
                  Choose team to view &amp; edit questions
                </label>
                <div className="relative">
                  <select
                    id="question-team"
                    value={selectedTeamId}
                    onChange={(event) => setSelectedTeamId(event.target.value)}
                    className="w-full appearance-none rounded-lg border border-solid border-[#E7EAF4] bg-white px-3 py-2 pr-8 text-left font-talent text-[16px] font-medium leading-[20px] text-[#1D264A] focus:outline-none focus:ring-2 focus:ring-[#2E9E5D]"
                  >
                    {participatingTeams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                  <CaretDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#5E6580]" />
                </div>
              </div>
            )}

            <QuestionsPanel
              questionsLoading={questionsLoading}
              questionsLoadError={questionsLoadError}
              visibleQuestions={visibleQuestions}
              adding={adding}
              tab={tab}
              selectedTeamId={selectedTeamId}
              sharedTeams={sharedTeams}
              orgSlug={orgSlug}
              onUpdateQuestion={onUpdateQuestion}
              onDeleteQuestion={onDeleteQuestion}
              onCreate={handleCreate}
              onCancelCreate={() => setAdding(false)}
              onStartCreate={() => setAdding(true)}
              onViewRatingScales={onViewRatingScales}
            />
          </>
        )}
      </div>
    </>
  )
}
